/**
 * Whether the words found anything worth calling an answer — and therefore whether a vector is bought.
 *
 * **What this replaces.** The rule was `byWords.length < 2`: escalate when the words returned fewer than
 * two rows. F156 measured that 2 against a corpus with no near-duplicates and no newsletters. Thomas's
 * mailbox is mostly newsletters, and a count is the wrong quantity anyway — *how many* the words found
 * says nothing about whether any of them is about the question. Two matches on `the` is two matches.
 *
 * **The insight this is designed around** (Thomas, 2026-09-27, after the display-name measurement): *for
 * the people I mail most, their name is the least discriminating word in my mailbox.* A question about
 * somebody you write to every week matches every row they are on — eleven of seventy-four, in the planted
 * corpus — and eleven matches is not eleven pieces of evidence. It is one piece of evidence that the
 * person exists, repeated. **A term matching a large share of what is held is not evidence, whatever
 * language it is in and whoever it names.**
 *
 * So the question is not *how many* but *did the words narrow anything*:
 *
 *  - nothing matched at all — there is nothing to rank, and the vector is the only thing left to ask;
 *  - the match set is a large **share** of what the caller can see — the words separated nothing, which
 *    is what a name, a newsletter sender or a stopword does;
 *  - the best row is weakly ranked — the words matched, but barely, on a fragment of the question.
 *
 * **Every number below is provisional and says so.** They are placed here, named and in one function, so
 * the first live run against a real mailbox can move them — rather than being a literal somewhere in a
 * query, which is where the last one hid for a month. `measuredOn` records what a value was last set
 * from, so a number nobody has re-measured is visible as such rather than looking decided.
 */

import { neverMeasured } from './measured.ts';

export interface WordsFound {
  /** How many rows the query matched in total — not how many this page returned. */
  matched: number;
  /** How many rows the caller could have matched: the corpus this question was asked against. */
  reachable: number;
  /** `ts_rank_cd` of the best row, or null when nothing matched. Cover density, so 0 upwards. */
  topRank: number | null;
}

export interface WeaknessRule {
  /** Above this share of the reachable corpus, a match set has separated nothing. */
  shareCeiling: number;
  /** Below this rank, the best row matched on a fragment. */
  rankFloor: number;
  /** What each number was last measured against. `null` means nobody has measured it yet. */
  measuredOn: string | null;
}

/**
 * **Not yet measured on anything that is a mailbox.** The share is set where a display name sat in the
 * planted corpus — eleven of seventy-four, which is 0.149 — so that case reads as weak rather than
 * strong, which is the behaviour Thomas asked to be designed for. The rank floor is deliberately at zero
 * until there is a live distribution to put it in: a rank floor chosen from 74 invented pointers would be
 * a number pretending to be a measurement, and zero is at least honestly inert.
 */
export const SHARE_CEILING = neverMeasured(
  0.12,
  'above this share of what a caller can reach, the words have separated nothing and a vector is bought',
);

export const RANK_FLOOR = neverMeasured(
  0,
  'below this cover-density rank the best row matched on a fragment. Inert at zero until a live distribution exists',
);

export const PROVISIONAL: WeaknessRule = {
  shareCeiling: SHARE_CEILING.value,
  rankFloor: RANK_FLOOR.value,
  measuredOn: SHARE_CEILING.measuredOn,
};

export interface Weakness {
  weak: boolean;
  /** Why, in a word, for the audit row and the gap sentence. A closed vocabulary. */
  because: 'nothing_matched' | 'matched_too_much' | 'ranked_too_low' | null;
}

export function wordsAreWeak(found: WordsFound, rule: WeaknessRule = PROVISIONAL): Weakness {
  if (found.matched === 0) return { weak: true, because: 'nothing_matched' };
  /**
   * A share is only meaningful over a corpus large enough to have one. In an almost-empty mailbox every
   * match is a large share of it, and escalating on that would buy a vector for the first ten pointers
   * somebody connects — when the words have in fact just answered perfectly.
   */
  if (found.reachable >= 50 && found.matched / found.reachable > rule.shareCeiling)
    return { weak: true, because: 'matched_too_much' };
  if (found.topRank !== null && found.topRank < rule.rankFloor)
    return { weak: true, because: 'ranked_too_low' };
  return { weak: false, because: null };
}
